/**
 * The stylized material for props: instanced and coloured per instance, swaying with the wind when
 * it has a weight, lit by the soft light, its own glow pulsing when asked (each instance in its own
 * time). Cheap enough for every prop on an older phone.
 */
import * as THREE from 'three';

import { LIGHT, WIND } from './glsl';
import { styleUniforms } from './uniforms';

export interface PropLook {
  color: number;
  /** How far the wind moves its tips: 0 for stones and machines. */
  wind?: number;
  emissive?: number;
  emissiveIntensity?: number;
  /** The geometry carries its own colours (a grass tuft: dark at the root, light at the tip). */
  vertexColors?: boolean;
  /** Thin blades are seen from both sides. */
  doubleSided?: boolean;
  /** The glow swells and fades, each instance in its own time (crystals being drawn from). */
  pulse?: boolean;
}

const vertexShader = /* glsl */ `
${WIND}
uniform vec3 uColor;
uniform float uWindWeight;
varying vec3 vNormal;
varying vec3 vView;
varying vec3 vColor;
varying float vPulse;
void main() {
  vec3 p = position;
  vec3 n = normal;
  vPulse = 1.0;
#ifdef USE_INSTANCING
#ifdef PULSE
  vPulse = 0.5 + 0.5 * sin(uTime * 2.4 + dot(instanceMatrix[3].xyz, vec3(12.9898, 78.233, 37.719)));
#endif
  p = windSway(p, instanceMatrix[3].xyz, uWindWeight);
  vec4 local = instanceMatrix * vec4(p, 1.0);
  n = mat3(instanceMatrix) * n;
#else
  p = windSway(p, vec3(0.0), uWindWeight);
  vec4 local = vec4(p, 1.0);
#endif
  vec4 mv = modelViewMatrix * local;
  vNormal = normalize(normalMatrix * n);
  vView = -mv.xyz;
  vColor = uColor;
#ifdef USE_INSTANCING_COLOR
  vColor *= instanceColor;
#endif
#ifdef USE_COLOR
  vColor *= color;
#endif
  gl_Position = projectionMatrix * mv;
}
`;

const fragmentShader = /* glsl */ `
${LIGHT}
uniform vec3 uEmissive;
varying vec3 vNormal;
varying vec3 vView;
varying vec3 vColor;
varying float vPulse;
void main() {
#ifdef PULSE
  vec3 glow = uEmissive * (0.15 + 1.35 * vPulse * vPulse);
#else
  vec3 glow = uEmissive;
#endif
  vec3 color = stylizedLight(vColor, normalize(vNormal), normalize(vView), 0.55) + glow;
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export function propMaterial(look: PropLook): THREE.ShaderMaterial {
  const emissive = new THREE.Color(look.emissive ?? 0x000000).multiplyScalar(
    look.emissiveIntensity ?? 1,
  );
  return new THREE.ShaderMaterial({
    uniforms: {
      uTime: styleUniforms.uTime,
      uWind: styleUniforms.uWind,
      uSunDir: styleUniforms.uSunDir,
      uUpDir: styleUniforms.uUpDir,
      uSunColor: styleUniforms.uSunColor,
      uSkyColor: styleUniforms.uSkyColor,
      uGroundColor: styleUniforms.uGroundColor,
      uRimColor: styleUniforms.uRimColor,
      uColor: { value: new THREE.Color(look.color) },
      uWindWeight: { value: look.wind ?? 0 },
      uEmissive: { value: emissive },
    },
    vertexShader,
    fragmentShader,
    defines: look.pulse ? { PULSE: '' } : {},
    vertexColors: look.vertexColors ?? false,
    side: look.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
  });
}
